<template>
  <div class="graded-followups-page py-3">
    <header class="gf-toolbar">
      <div class="gf-toolbar__title">
        <span class="gf-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Graded Follow Ups</h1>
          <p>Graded follow-ups awaiting feedback review</p>
        </div>
      </div>
      <div class="gf-toolbar__actions">
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadFollowUps(currentPage)"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="gf-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="gf-alert">{{ successMessage }}</CAlert>

    <section class="gf-section">
      <header class="gf-section__head">
        <h2>Filters</h2>
        <small class="gf-section__hint">{{ totalLabel }}</small>
      </header>
      <div class="gf-section__body">
        <div class="gf-filters">
          <div class="gf-field">
            <label class="gf-label">Date Range</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="gf-field">
            <label class="gf-label">Phone</label>
            <AppInput
              no-margin
              :value="filters.phone"
              placeholder="Search by phone…"
              @update:value="filters.phone = $event"
              @keydown.enter.native="applyFilters"
            />
          </div>
          <div class="gf-field gf-field--action">
            <label class="gf-label" aria-hidden="true">&nbsp;</label>
            <button
              type="button"
              class="gf-btn gf-btn--primary"
              :disabled="isLoading"
              @click="applyFilters"
            >
              {{ isLoading ? '…' : 'Apply' }}
            </button>
          </div>
          <div class="gf-field gf-field--action">
            <label class="gf-label" aria-hidden="true">&nbsp;</label>
            <button
              type="button"
              class="gf-btn gf-btn--ghost"
              :disabled="isLoading"
              @click="resetFilters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Graded Follow Ups"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      :show-count="false"
      empty-text="No graded follow-ups for the current filters."
    >
      <template #toolbar>
        <span class="gf-table-hint">Showing {{ pageRangeLabel }}</span>
      </template>

      <template #cell-grade="{ value }">
        <span :class="['gf-grade-pill', gradeClass(value)]">{{ gradeLabel(value) }}</span>
      </template>

      <template #cell-comments="{ value }">
        <span class="gf-comments" :title="value || ''">{{ value || '—' }}</span>
      </template>

      <template #cell-actions="{ row }">
        <div class="gf-actions">
          <div class="gf-actions__field">
            <label class="gf-actions__label">Feedback Grade</label>
            <AppDropdown
              :value="row.draft.feedback_grade"
              :options="gradeOptions"
              placeholder="Select"
              @input="updateDraft(row.id, 'feedback_grade', $event)"
            />
          </div>
          <div class="gf-actions__field">
            <label class="gf-actions__label">Feedback Comments</label>
            <AppInput
              no-margin
              :value="row.draft.feedback_comments"
              placeholder="Followup Comment"
              @update:value="updateDraft(row.id, 'feedback_comments', $event)"
            />
          </div>
          <div class="gf-actions__field">
            <label class="gf-actions__label">Status</label>
            <AppDropdown
              :value="row.draft.status"
              :options="statusOptions"
              placeholder="Select"
              @input="updateDraft(row.id, 'status', $event)"
            />
          </div>
          <div class="gf-actions__field gf-actions__field--button">
            <label class="gf-actions__label" aria-hidden="true">&nbsp;</label>
            <button
              type="button"
              class="gf-save-btn"
              :disabled="savingId === row.id"
              @click="saveRow(row.id)"
            >
              {{ savingId === row.id ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </div>
      </template>
    </BaseTable>

    <div v-if="!isLoading && followUps.length && lastPage > 1" class="gf-pagination-bar">
      <span class="gf-pagination-info">{{ pageRangeLabel }}</span>
      <AppPager :current-page="currentPage" :last-page="lastPage" @change="goToPage" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppInput from '@/components/AppInput.vue'
import AppPager from '@/components/AppPager.vue'
import BaseTable from '@/components/BaseTable.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'closed', label: 'Closed' },
  { value: 'completed', label: 'Completed' }
]

export default {
  name: 'GradedFollowUps',
  components: { AppDropdown, AppInput, AppPager, BaseTable, DateRangePicker },
  data () {
    return {
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      followUps: [],
      drafts: {},
      savingId: null,
      currentPage: 1,
      lastPage: 1,
      perPage: 30,
      total: 0,
      gradeOptions: [],
      statusOptions: STATUS_OPTIONS,
      filters: {
        fromDate: '',
        toDate: '',
        phone: ''
      },
      columns: [
        { key: 'added_time', label: 'Added Time' },
        { key: 'contact_name', label: 'Contact Name' },
        { key: 'contact_number', label: 'Contact Number' },
        { key: 'comments', label: 'Comments', width: '260px' },
        { key: 'grade', label: 'Grade' },
        { key: 'actions', label: 'Actions', width: '720px' }
      ]
    }
  },
  computed: {
    totalLabel () {
      return this.total ? `${this.total} total` : ''
    },
    pageRangeLabel () {
      if (!this.followUps.length) return '0'
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = start + this.followUps.length - 1
      return `${start}–${end} of ${this.total}`
    },
    dateRange: {
      get () {
        return { fromDate: this.filters.fromDate, toDate: this.filters.toDate }
      },
      set (value) {
        this.filters.fromDate = (value && value.fromDate) || ''
        this.filters.toDate = (value && value.toDate) || ''
      }
    },
    hasSearchFilters () {
      return Boolean(this.filters.fromDate && this.filters.toDate)
    },
    tableRows () {
      return this.followUps.map((followUp) => {
        const draft = this.drafts[followUp.id] || this.buildDraft(followUp)
        return {
          id: followUp.id,
          added_time: this.formatDateTime(followUp.created_at),
          contact_name: (followUp.contact && followUp.contact.name) || '—',
          contact_number: (followUp.contact && followUp.contact.phone) || '—',
          comments: followUp.comments || '',
          grade: followUp.grade || '',
          draft
        }
      })
    }
  },
  created () {
    this.loadGradeOptions()
    this.loadFollowUps()
  },
  methods: {
    formatDateTime (timestamp) {
      if (!timestamp) return ''
      const ms = typeof timestamp === 'number' ? timestamp * 1000 : Date.parse(timestamp)
      if (!Number.isFinite(ms)) return ''
      const d = new Date(ms)
      const pad = (n) => String(n).padStart(2, '0')
      const hour24 = d.getHours()
      const hour12 = ((hour24 + 11) % 12) + 1
      const ampm = hour24 >= 12 ? 'PM' : 'AM'
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(hour12)}:${pad(d.getMinutes())} ${ampm}`
    },
    gradeClass (grade) {
      const value = String(grade || '').toLowerCase()
      if (!value) return 'gf-grade-pill--muted'
      if (value.includes('5')) return 'gf-grade-pill--green'
      if (value.includes('4')) return 'gf-grade-pill--blue'
      if (value.includes('3')) return 'gf-grade-pill--slate'
      if (value.includes('2')) return 'gf-grade-pill--amber'
      if (value.includes('1')) return 'gf-grade-pill--red'
      return 'gf-grade-pill--muted'
    },
    gradeLabel (grade) {
      if (!grade) return '—'
      const match = this.gradeOptions.find((opt) => opt.value === grade)
      return match ? match.label : grade
    },
    buildDraft (followUp) {
      const draft = {
        feedback_grade: followUp.feedback_grade || followUp.grade || '',
        feedback_comments: followUp.feedback_comments || '',
        status: followUp.status || 'pending'
      }
      this.$set(this.drafts, followUp.id, draft)
      return draft
    },
    updateDraft (id, field, value) {
      const existing = this.drafts[id] || {}
      this.$set(this.drafts, id, { ...existing, [field]: value })
    },
    loadGradeOptions () {
      axios.get(`${this.$apiAdress}/api/followup_grade_list`)
        .then((response) => {
          const list = Array.isArray(response.data) ? response.data : []
          this.gradeOptions = list.map((item) => ({
            value: item.slug || item.name || '',
            label: item.name || item.slug || ''
          })).filter((opt) => opt.value)
        })
        .catch((error) => {
          console.log(error)
        })
    },
    loadFollowUps (page = 1) {
      this.errorMessage = ''
      this.successMessage = ''
      this.isLoading = true

      if (this.hasSearchFilters || this.filters.phone) {
        this.searchFollowUps(page)
        return
      }

      axios.post(`${this.$apiAdress}/api/mdi_grade_followups?page=${page}`)
        .then((response) => {
          this.applyPaginatedResponse(response.data)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load graded follow-ups.'
          this.followUps = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    searchFollowUps (page = 1) {
      if (!this.filters.fromDate || !this.filters.toDate) {
        this.errorMessage = 'Please select both From and To dates to search.'
        this.isLoading = false
        return
      }
      axios.post(`${this.$apiAdress}/api/followups/grade/search`, {
        start_date: this.filters.fromDate,
        end_date: this.filters.toDate,
        phone: this.filters.phone || ''
      })
        .then((response) => {
          const list = Array.isArray(response.data) ? response.data : []
          this.drafts = {}
          this.followUps = list
          this.total = list.length
          this.currentPage = page
          this.lastPage = 1
          this.perPage = list.length || this.perPage
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to search graded follow-ups.'
          this.followUps = []
          this.total = 0
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    applyPaginatedResponse (data) {
      const payload = data || {}
      const list = Array.isArray(payload.data) ? payload.data : []
      this.drafts = {}
      this.followUps = list
      this.currentPage = Number(payload.current_page || 1)
      this.lastPage = Number(payload.last_page || 1)
      this.total = Number(payload.total || list.length)
      this.perPage = Number(payload.per_page || this.perPage)
    },
    applyFilters () {
      this.currentPage = 1
      this.loadFollowUps(1)
    },
    resetFilters () {
      this.filters = { fromDate: '', toDate: '', phone: '' }
      this.currentPage = 1
      this.loadFollowUps(1)
    },
    goToPage (page) {
      if (page < 1 || page > this.lastPage) return
      this.loadFollowUps(page)
    },
    saveRow (id) {
      const draft = this.drafts[id]
      if (!draft) return
      if (!draft.status) {
        this.errorMessage = 'Status is required.'
        return
      }

      this.errorMessage = ''
      this.successMessage = ''
      this.savingId = id

      axios.put(`${this.$apiAdress}/api/followups/${id}`, {
        id,
        status: draft.status,
        feedback_grade: draft.feedback_grade || '',
        feedback_comments: draft.feedback_comments || ''
      })
        .then((response) => {
          this.successMessage = (response && response.data && response.data.message) || 'Saved successfully.'
          this.applyLocalUpdate(id, draft)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to save follow-up.'
        })
        .finally(() => {
          this.savingId = null
        })
    },
    applyLocalUpdate (id, draft) {
      if (draft.status === 'closed' || draft.status === 'completed') {
        this.followUps = this.followUps.filter((item) => item.id !== id)
        this.total = Math.max(0, this.total - 1)
        this.$delete(this.drafts, id)
        return
      }
      const index = this.followUps.findIndex((item) => item.id === id)
      if (index === -1) return
      const updated = {
        ...this.followUps[index],
        status: draft.status,
        feedback_grade: draft.feedback_grade,
        feedback_comments: draft.feedback_comments
      }
      this.$set(this.followUps, index, updated)
    }
  }
}
</script>

<style scoped>
.graded-followups-page {
  padding: 0.25rem 0;
}

.gf-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.gf-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.gf-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.gf-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.gf-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.gf-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.gf-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.gf-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.gf-toolbar__actions >>> .btn.refresh-btn:focus { outline: none; }

.gf-toolbar__actions >>> .btn.refresh-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.gf-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.gf-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.gf-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: gf-spin 0.9s linear infinite;
}

@keyframes gf-spin {
  to { transform: rotate(360deg); }
}

.gf-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.gf-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.gf-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.gf-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.gf-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.gf-section__body {
  padding: 1rem;
}

.gf-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.gf-field {
  display: flex;
  flex-direction: column;
  flex: 1 1 220px;
  min-width: 0;
}

.gf-field--action {
  flex: 0 0 auto;
  min-width: 110px;
}

.gf-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.gf-btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0 1rem;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, filter 0.15s ease;
}

.gf-btn:focus { outline: none; }
.gf-btn::-moz-focus-inner { border: 0; }

.gf-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.gf-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.gf-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.gf-btn--primary:disabled {
  opacity: 0.75;
  cursor: progress;
}

.gf-btn--ghost:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.gf-table-hint {
  font-size: 0.78rem;
  color: #6b7280;
}

.gf-comments {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.gf-grade-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.gf-grade-pill--green { background: #dff5ea; color: #0e5b46; }
.gf-grade-pill--blue  { background: #e0edff; color: #1e40af; }
.gf-grade-pill--slate { background: #e2e8f0; color: #334155; }
.gf-grade-pill--amber { background: #fef3c7; color: #92400e; }
.gf-grade-pill--red   { background: #fee2e2; color: #b91c1c; }
.gf-grade-pill--muted { background: #f1f5f9; color: #64748b; }

.gf-actions {
  display: grid;
  grid-template-columns: minmax(160px, 1.1fr) minmax(180px, 1.3fr) minmax(140px, 1fr) auto;
  gap: 0.65rem;
  align-items: end;
  min-width: 660px;
}

.gf-actions__field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.gf-actions__field--button { min-width: 0; }

.gf-actions__label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.02em;
}

.gf-save-btn {
  height: 36px;
  padding: 0 1.1rem;
  border-radius: 8px;
  border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.gf-save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.gf-save-btn:focus { outline: none; }
.gf-save-btn::-moz-focus-inner { border: 0; }

.gf-save-btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.gf-save-btn:disabled {
  opacity: 0.75;
  cursor: progress;
}

.gf-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.85rem;
}

.gf-pagination-info {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
}
</style>
